import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types for touchpoint states
export interface PulseCheckTouchpoint {
  id: string;
  employeeId: string;
  date: string;
  status: "pending" | "completed" | "overdue";
  responses: {
    question: string;
    answer: string;
    rating?: number;
  }[];
  notes: string;
  completedBy?: string;
  completedOn?: string;
}

export interface ProbationaryReviewTouchpoint {
  id: string;
  employeeId: string;
  reviewType: "30day" | "60day" | "90day";
  date: string;
  status: "pending" | "in-progress" | "completed" | "overdue";
  performanceRatings: {
    categoryId: string;
    categoryName: string;
    rating: number;
    comments: string;
  }[];
  overallRating: number;
  strengthsObservations: string;
  developmentAreas: string;
  actionPlan: string;
  managerComments: string;
  employeeComments: string;
  // Signature fields
  managerSignatureFileName: string | null;
  managerSignatureImageUrl: string | null;
  isManagerSignatureUpdated: boolean;
  employeeSignatureFileName: string | null;
  employeeSignatureImageUrl: string | null;
  isEmployeeSignatureUpdated: boolean;
  completedBy?: string;
  completedOn?: string;
}

export interface PreferredMethodChecklistTouchpoint {
  id: string;
  employeeId: string;
  date: string;
  status: "pending" | "in-progress" | "completed" | "overdue";
  tasks: {
    taskId: string;
    taskName: string;
    completed: boolean;
    preferredMethod: string;
    notes: string;
  }[];
  overallNotes: string;
  // Signature fields
  trainerSignatureFileName: string | null;
  trainerSignatureImageUrl: string | null;
  isTrainerSignatureUpdated: boolean;
  employeeSignatureFileName: string | null;
  employeeSignatureImageUrl: string | null;
  isEmployeeSignatureUpdated: boolean;
  completedBy?: string;
  completedOn?: string;
}

// Using Employee interface from manageEmployeesSlice

// Types for admin configurations
export interface AdminConfiguration {
  pulseCheckFrequency: number; // Days between pulse checks
  probationaryReviewSchedule: {
    firstReview: number; // Days after hire date
    secondReview: number;
    thirdReview: number;
  };
  preferredMethodChecklistSchedule: {
    initialChecklist: number; // Days after hire date
    followupChecklist?: number;
  };
  notificationSettings: {
    emailEnabled: boolean;
    reminderDays: number; // Days before due date to send reminder
  };
  customFields: {
    fieldId: string;
    fieldName: string;
    fieldType: "text" | "date" | "number" | "select";
    options?: string[]; // For select fields
    required: boolean;
  }[];
  performanceCategories: {
    id: string;
    name: string;
    description: string;
  }[];
}

// Dashboard analytics data
interface DashboardAnalytics {
  activeEmployees: number;
  completedOnboardings: number;
  inProgressOnboardings: number;
  upcomingTouchpoints: number;
  overdueItems: number;
  averageOnboardingProgress: number;
  recentActivity: {
    id: string;
    employeeId: string;
    employeeName: string;
    activityType: string;
    date: string;
    details: string;
  }[];
}

// Filter options for the dashboard and employee lists
interface FilterOptions {
  departments: string[];
  locations: string[];
  managers: string[];
  statuses: string[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// Main state interface
interface NewHireOnboardingState {
  pulseChecks: PulseCheckTouchpoint[];
  probationaryReviews: ProbationaryReviewTouchpoint[];
  preferredMethodChecklists: PreferredMethodChecklistTouchpoint[];
  adminConfiguration: AdminConfiguration;
  dashboardAnalytics: DashboardAnalytics;
  filterOptions: FilterOptions;
  selectedFilters: {
    department: string;
    location: string;
    manager: string;
    status: string;
    dateRange: {
      startDate: string;
      endDate: string;
    };
  };
  selectedEmployeeId: string | null;
  selectedTouchpoint: {
    type:
      | "pulseCheck"
      | "probationaryReview"
      | "preferredMethodChecklist"
      | null;
    id: string | null;
  };
  touchpointFormState: "new" | "viewing" | "editing";
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  activeTab: string;
}

// Initial state
const initialState: NewHireOnboardingState = {
  pulseChecks: [],
  probationaryReviews: [],
  preferredMethodChecklists: [],
  adminConfiguration: {
    pulseCheckFrequency: 14, // Default: every 2 weeks
    probationaryReviewSchedule: {
      firstReview: 30,
      secondReview: 60,
      thirdReview: 90,
    },
    preferredMethodChecklistSchedule: {
      initialChecklist: 7,
      followupChecklist: 21,
    },
    notificationSettings: {
      emailEnabled: true,
      reminderDays: 3,
    },
    customFields: [],
    performanceCategories: [
      {
        id: "perf1",
        name: "Job Knowledge",
        description: "Understanding of role responsibilities",
      },
      {
        id: "perf2",
        name: "Quality of Work",
        description: "Accuracy and thoroughness of work",
      },
      {
        id: "perf3",
        name: "Communication",
        description: "Verbal and written communication skills",
      },
      {
        id: "perf4",
        name: "Teamwork",
        description: "Ability to work effectively with others",
      },
      {
        id: "perf5",
        name: "Adaptability",
        description: "Ability to adjust to change",
      },
    ],
  },
  dashboardAnalytics: {
    activeEmployees: 0,
    completedOnboardings: 0,
    inProgressOnboardings: 0,
    upcomingTouchpoints: 0,
    overdueItems: 0,
    averageOnboardingProgress: 0,
    recentActivity: [],
  },
  filterOptions: {
    departments: [],
    locations: [],
    managers: [],
    statuses: ["active", "onLeave", "terminated"],
    dateRange: {
      startDate: "",
      endDate: "",
    },
  },
  selectedFilters: {
    department: "",
    location: "",
    manager: "",
    status: "",
    dateRange: {
      startDate: "",
      endDate: "",
    },
  },
  selectedEmployeeId: null,
  selectedTouchpoint: {
    type: null,
    id: null,
  },
  touchpointFormState: "new",
  loading: false,
  error: null,
  successMessage: null,
  activeTab: "dashboard",
};

// Create the slice
const newHireOnboardingSlice = createSlice({
  name: "newHireOnboarding",
  initialState,
  reducers: {
    // Employee selection
    setSelectedEmployeeId: (state, action: PayloadAction<string | null>) => {
      state.selectedEmployeeId = action.payload;
    },

    // PulseCheck touchpoint actions
    setPulseChecks: (state, action: PayloadAction<PulseCheckTouchpoint[]>) => {
      state.pulseChecks = action.payload;
    },
    addPulseCheck: (state, action: PayloadAction<PulseCheckTouchpoint>) => {
      state.pulseChecks.push(action.payload);
    },
    updatePulseCheck: (state, action: PayloadAction<PulseCheckTouchpoint>) => {
      const index = state.pulseChecks.findIndex(
        (pc) => pc.id === action.payload.id
      );
      if (index >= 0) {
        state.pulseChecks[index] = action.payload;
      }
    },

    // Probationary Review touchpoint actions
    setProbationaryReviews: (
      state,
      action: PayloadAction<ProbationaryReviewTouchpoint[]>
    ) => {
      state.probationaryReviews = action.payload;
    },
    addProbationaryReview: (
      state,
      action: PayloadAction<ProbationaryReviewTouchpoint>
    ) => {
      state.probationaryReviews.push(action.payload);
    },
    updateProbationaryReview: (
      state,
      action: PayloadAction<ProbationaryReviewTouchpoint>
    ) => {
      const index = state.probationaryReviews.findIndex(
        (pr) => pr.id === action.payload.id
      );
      if (index >= 0) {
        state.probationaryReviews[index] = action.payload;
      }
    },

    // Signature-related actions for probationary reviews
    setManagerSignature: (
      state,
      action: PayloadAction<{
        reviewId: string;
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean;
      }>
    ) => {
      const { reviewId, fileName, imageUrl, isNewUpload } = action.payload;
      const index = state.probationaryReviews.findIndex(
        (review) => review.id === reviewId
      );

      if (index >= 0) {
        state.probationaryReviews[index].managerSignatureFileName = fileName;
        state.probationaryReviews[index].managerSignatureImageUrl =
          imageUrl || null;

        if (isNewUpload) {
          state.probationaryReviews[index].isManagerSignatureUpdated = true;
        } else if (isNewUpload === false) {
          state.probationaryReviews[index].isManagerSignatureUpdated = false;
        }
      }
    },

    setEmployeeSignature: (
      state,
      action: PayloadAction<{
        reviewId: string;
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean;
      }>
    ) => {
      const { reviewId, fileName, imageUrl, isNewUpload } = action.payload;
      const index = state.probationaryReviews.findIndex(
        (review) => review.id === reviewId
      );

      if (index >= 0) {
        state.probationaryReviews[index].employeeSignatureFileName = fileName;
        state.probationaryReviews[index].employeeSignatureImageUrl =
          imageUrl || null;

        if (isNewUpload) {
          state.probationaryReviews[index].isEmployeeSignatureUpdated = true;
        } else if (isNewUpload === false) {
          state.probationaryReviews[index].isEmployeeSignatureUpdated = false;
        }
      }
    },

    resetManagerSignatureFlag: (state, action: PayloadAction<string>) => {
      const reviewId = action.payload;
      const index = state.probationaryReviews.findIndex(
        (review) => review.id === reviewId
      );

      if (index >= 0) {
        state.probationaryReviews[index].isManagerSignatureUpdated = false;
      }
    },

    resetEmployeeSignatureFlag: (state, action: PayloadAction<string>) => {
      const reviewId = action.payload;
      const index = state.probationaryReviews.findIndex(
        (review) => review.id === reviewId
      );

      if (index >= 0) {
        state.probationaryReviews[index].isEmployeeSignatureUpdated = false;
      }
    },

    // Preferred Method Checklist touchpoint actions
    setPreferredMethodChecklists: (
      state,
      action: PayloadAction<PreferredMethodChecklistTouchpoint[]>
    ) => {
      state.preferredMethodChecklists = action.payload;
    },
    addPreferredMethodChecklist: (
      state,
      action: PayloadAction<PreferredMethodChecklistTouchpoint>
    ) => {
      state.preferredMethodChecklists.push(action.payload);
    },
    updatePreferredMethodChecklist: (
      state,
      action: PayloadAction<PreferredMethodChecklistTouchpoint>
    ) => {
      const index = state.preferredMethodChecklists.findIndex(
        (pmc) => pmc.id === action.payload.id
      );
      if (index >= 0) {
        state.preferredMethodChecklists[index] = action.payload;
      }
    },

    // Preferred Method Checklist signature-related actions
    setTrainerSignature: (
      state,
      action: PayloadAction<{
        checklistId: string;
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean;
      }>
    ) => {
      const { checklistId, fileName, imageUrl, isNewUpload } = action.payload;
      const index = state.preferredMethodChecklists.findIndex(
        (checklist) => checklist.id === checklistId
      );

      if (index >= 0) {
        state.preferredMethodChecklists[index].trainerSignatureFileName =
          fileName;
        state.preferredMethodChecklists[index].trainerSignatureImageUrl =
          imageUrl || null;

        if (isNewUpload) {
          state.preferredMethodChecklists[index].isTrainerSignatureUpdated =
            true;
        } else if (isNewUpload === false) {
          state.preferredMethodChecklists[index].isTrainerSignatureUpdated =
            false;
        }
      }
    },

    setEmployeeChecklistSignature: (
      state,
      action: PayloadAction<{
        checklistId: string;
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean;
      }>
    ) => {
      const { checklistId, fileName, imageUrl, isNewUpload } = action.payload;
      const index = state.preferredMethodChecklists.findIndex(
        (checklist) => checklist.id === checklistId
      );

      if (index >= 0) {
        state.preferredMethodChecklists[index].employeeSignatureFileName =
          fileName;
        state.preferredMethodChecklists[index].employeeSignatureImageUrl =
          imageUrl || null;

        if (isNewUpload) {
          state.preferredMethodChecklists[index].isEmployeeSignatureUpdated =
            true;
        } else if (isNewUpload === false) {
          state.preferredMethodChecklists[index].isEmployeeSignatureUpdated =
            false;
        }
      }
    },

    resetTrainerSignatureFlag: (state, action: PayloadAction<string>) => {
      const checklistId = action.payload;
      const index = state.preferredMethodChecklists.findIndex(
        (checklist) => checklist.id === checklistId
      );

      if (index >= 0) {
        state.preferredMethodChecklists[index].isTrainerSignatureUpdated =
          false;
      }
    },

    resetEmployeeChecklistSignatureFlag: (
      state,
      action: PayloadAction<string>
    ) => {
      const checklistId = action.payload;
      const index = state.preferredMethodChecklists.findIndex(
        (checklist) => checklist.id === checklistId
      );

      if (index >= 0) {
        state.preferredMethodChecklists[index].isEmployeeSignatureUpdated =
          false;
      }
    },

    // Admin configuration actions
    setAdminConfiguration: (
      state,
      action: PayloadAction<AdminConfiguration>
    ) => {
      state.adminConfiguration = action.payload;
    },
    updateAdminConfiguration: (
      state,
      action: PayloadAction<Partial<AdminConfiguration>>
    ) => {
      state.adminConfiguration = {
        ...state.adminConfiguration,
        ...action.payload,
      };
    },

    // Dashboard analytics actions
    setDashboardAnalytics: (
      state,
      action: PayloadAction<DashboardAnalytics>
    ) => {
      state.dashboardAnalytics = action.payload;
    },

    // Filter-related actions
    setFilterOptions: (state, action: PayloadAction<FilterOptions>) => {
      state.filterOptions = action.payload;
    },
    setSelectedFilters: (
      state,
      action: PayloadAction<
        Partial<{
          department: string;
          location: string;
          manager: string;
          status: string;
          dateRange: {
            startDate: string;
            endDate: string;
          };
        }>
      >
    ) => {
      state.selectedFilters = { ...state.selectedFilters, ...action.payload };
    },
    resetFilters: (state) => {
      state.selectedFilters = initialState.selectedFilters;
    },

    // Touchpoint selection
    setSelectedTouchpoint: (
      state,
      action: PayloadAction<{
        type:
          | "pulseCheck"
          | "probationaryReview"
          | "preferredMethodChecklist"
          | null;
        id: string | null;
      }>
    ) => {
      state.selectedTouchpoint = action.payload;
    },

    // Form state actions
    setTouchpointFormState: (
      state,
      action: PayloadAction<"new" | "viewing" | "editing">
    ) => {
      state.touchpointFormState = action.payload;
    },

    // UI state actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },

    // Reset state actions
    resetNewHireOnboardingState: (state) => {
      // Reset all state to initial values
      Object.assign(state, initialState);
    },

    // Reset specific form states
    resetPulseCheckForm: (state) => {
      // Keep selected employee while resetting only the form-related state
      state.selectedTouchpoint = {
        type: null,
        id: null,
      };
      state.touchpointFormState = "new";
    },
    resetProbationaryReviewForm: (state) => {
      // Keep selected employee while resetting only the form-related state
      state.selectedTouchpoint = {
        type: null,
        id: null,
      };
      state.touchpointFormState = "new";
    },
    resetPreferredMethodChecklistForm: (state) => {
      // Keep selected employee while resetting only the form-related state
      state.selectedTouchpoint = {
        type: null,
        id: null,
      };
      state.touchpointFormState = "new";
    },
  },
});

// Export actions
export const {
  // Employee selection
  setSelectedEmployeeId,

  // PulseCheck actions
  setPulseChecks,
  addPulseCheck,
  updatePulseCheck,

  // Probationary Review actions
  setProbationaryReviews,
  addProbationaryReview,
  updateProbationaryReview,
  setManagerSignature,
  setEmployeeSignature,
  resetManagerSignatureFlag,
  resetEmployeeSignatureFlag,

  // Preferred Method Checklist actions
  setPreferredMethodChecklists,
  addPreferredMethodChecklist,
  updatePreferredMethodChecklist,
  setTrainerSignature,
  setEmployeeChecklistSignature,
  resetTrainerSignatureFlag,
  resetEmployeeChecklistSignatureFlag,

  // Admin config actions
  setAdminConfiguration,
  updateAdminConfiguration,

  // Dashboard actions
  setDashboardAnalytics,

  // Filter actions
  setFilterOptions,
  setSelectedFilters,
  resetFilters,

  // Touchpoint selection
  setSelectedTouchpoint,
  setTouchpointFormState,

  // UI state actions
  setLoading,
  setError,
  setSuccessMessage,
  clearError,
  clearSuccessMessage,
  setActiveTab,

  // Reset actions
  resetNewHireOnboardingState,
  resetPulseCheckForm,
  resetProbationaryReviewForm,
  resetPreferredMethodChecklistForm,
} = newHireOnboardingSlice.actions;

export default newHireOnboardingSlice.reducer;
